/**
 * Shared budget for automatic loading-recovery actions - the page reloads done by
 * useStuckLoadingReload and the retry navigations done by useConnectionErrorRetry.
 *
 * Both draw from one per-tab counter so that, against a backend that is genuinely down
 * rather than just cold, the two mechanisms together still can't loop forever. The count
 * is cleared outright once sign-in succeeds (clearRecoveryBudget), so the healthy path
 * always re-arms immediately; the STALE_MS window is only a back-off for the case where
 * recovery never reaches a good state. (NN-3133)
 */

const STORAGE_KEY = 'fgfe-loading-watchdog';
const STALE_MS = 3 * 60 * 1000;

export const MAX_RECOVERY_ATTEMPTS = 3;

const read = () => {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        if (parsed && typeof parsed.n === 'number' && typeof parsed.t === 'number') {
            return parsed;
        }
    } catch {
        // sessionStorage unavailable or unparseable - treat as a clean slate
    }
    return { n: 0, t: 0 };
};

/**
 * How many automatic recovery attempts have already been spent in this tab, or 0 if the
 * last one is old enough to be considered unrelated to the current problem.
 * @returns {number}
 */
export const recoveryAttemptsUsed = () => {
    const { n, t } = read();
    return (Date.now() - t) > STALE_MS ? 0 : n;
};

/**
 * Record that another automatic recovery attempt is about to happen. Best effort: if
 * sessionStorage can't be written the cap simply isn't enforced.
 */
export const recordRecoveryAttempt = () => {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ n: recoveryAttemptsUsed() + 1, t: Date.now() }));
    } catch {
        // best effort only
    }
};

/**
 * Clear the budget. Call this once the app has reached a known-good state (a successful
 * sign-in) so that a later, unrelated stall starts again from a full budget.
 */
export const clearRecoveryBudget = () => {
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // best effort only
    }
};
